/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateJobTicketRequest } from '../models/create-job-ticket-request';
import { GenericResponse } from '../models/generic-response';
import { GetJobTicketsResponse } from '../models/get-job-tickets-response';
import { JobTicketFileResponse } from '../models/job-ticket-file-response';
import { JobTicketResponse } from '../models/job-ticket-response';
import { Priority } from '../models/priority';
import { UpdateJobTicketRequest } from '../models/update-job-ticket-request';

/**
 * job ticket apis
 */
@Injectable({ providedIn: 'root' })
export class JobTicketService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getJobTickets()` */
  static readonly GetJobTicketsPath = '/api/v1/job-tickets';

  /**
   * get all job tickets.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getJobTickets()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobTickets$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a filter to the JobTicket list
       *  - serialNumber
       *  - requester
       *  - location
       *  - date
       */
      search?: string;

      /**
       * This adds a filter to the JobTicket list
       *  - priority Level
       */
      JobTicketByPriority?: Priority;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetJobTicketsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.GetJobTicketsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('JobTicketByPriority', params.JobTicketByPriority, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetJobTicketsResponse>;
        })
      );
  }

  /**
   * get all job tickets.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getJobTickets$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobTickets(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a filter to the JobTicket list
       *  - serialNumber
       *  - requester
       *  - location
       *  - date
       */
      search?: string;

      /**
       * This adds a filter to the JobTicket list
       *  - priority Level
       */
      JobTicketByPriority?: Priority;
    },
    context?: HttpContext
  ): Observable<GetJobTicketsResponse> {
    return this.getJobTickets$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetJobTicketsResponse>): GetJobTicketsResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createJobTicket()` */
  static readonly CreateJobTicketPath = '/api/v1/job-tickets';

  /**
   * create new job ticket.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createJobTicket()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createJobTicket$Response(
    params: {
      body: CreateJobTicketRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<JobTicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.CreateJobTicketPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<JobTicketResponse>;
        })
      );
  }

  /**
   * create new job ticket.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createJobTicket$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createJobTicket(
    params: {
      body: CreateJobTicketRequest;
    },
    context?: HttpContext
  ): Observable<JobTicketResponse> {
    return this.createJobTicket$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<JobTicketResponse>): JobTicketResponse => r.body
      )
    );
  }

  /** Path part for operation `getJobTicketById()` */
  static readonly GetJobTicketByIdPath = '/api/v1/job-tickets/{id}';

  /**
   * get job ticket by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getJobTicketById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobTicketById$Response(
    params: {
      /**
       * The job ticket id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<JobTicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.GetJobTicketByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<JobTicketResponse>;
        })
      );
  }

  /**
   * get job ticket by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getJobTicketById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getJobTicketById(
    params: {
      /**
       * The job ticket id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<JobTicketResponse> {
    return this.getJobTicketById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<JobTicketResponse>): JobTicketResponse => r.body
      )
    );
  }

  /** Path part for operation `disableJobTicketById()` */
  static readonly DisableJobTicketByIdPath = '/api/v1/job-tickets/{id}';

  /**
   * disable job ticket by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableJobTicketById()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableJobTicketById$Response(
    params: {
      /**
       * The job ticket id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.DisableJobTicketByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable job ticket by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableJobTicketById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableJobTicketById(
    params: {
      /**
       * The job ticket id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableJobTicketById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateJobTicket()` */
  static readonly UpdateJobTicketPath = '/api/v1/job-tickets/{id}';

  /**
   * update job ticket by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateJobTicket()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateJobTicket$Response(
    params: {
      /**
       * The job ticket id that needs to be updated
       */
      id: number;
      body: UpdateJobTicketRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<JobTicketResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.UpdateJobTicketPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<JobTicketResponse>;
        })
      );
  }

  /**
   * update job ticket by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateJobTicket$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateJobTicket(
    params: {
      /**
       * The job ticket id that needs to be updated
       */
      id: number;
      body: UpdateJobTicketRequest;
    },
    context?: HttpContext
  ): Observable<JobTicketResponse> {
    return this.updateJobTicket$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<JobTicketResponse>): JobTicketResponse => r.body
      )
    );
  }

  /** Path part for operation `uploadPdf()` */
  static readonly UploadPdfPath = '/api/v1/job-tickets/{id}/upload-pdf';

  /**
   * upload pdf.
   *
   * this endpoint is for uploading pdf file to job ticket
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadPdf()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadPdf$Response(
    params: {
      id: number;
      body: {
        files?: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<JobTicketFileResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.UploadPdfPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<JobTicketFileResponse>;
        })
      );
  }

  /**
   * upload pdf.
   *
   * this endpoint is for uploading pdf file to job ticket
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadPdf$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadPdf(
    params: {
      id: number;
      body: {
        files?: Blob;
      };
    },
    context?: HttpContext
  ): Observable<JobTicketFileResponse> {
    return this.uploadPdf$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<JobTicketFileResponse>): JobTicketFileResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `downloadPdf()` */
  static readonly DownloadPdfPath = '/api/v1/job-tickets/{id}/download-pdf';

  /**
   * download pdf.
   *
   * this endpoint is for downloading pdf file from job ticket
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadPdf()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadPdf$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      JobTicketService.DownloadPdfPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'blob', accept: 'application/*', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * download pdf.
   *
   * this endpoint is for downloading pdf file from job ticket
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadPdf$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadPdf(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadPdf$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
